import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import "./CompanySalesSettings.css";

const API_URL = "/api";

const emptyForm = {
  require_open_cash_register: true,
  allow_sales_without_stock: false,
  require_customer: false,
  allow_product_discounts: true,
  max_discount_percent: 0,
  default_payment_method: "cash",
  auto_print_receipt: false,
  allow_returns: true,
  is_active: true,
};

const normalizeSalesSettings = (data = {}) => ({
  ...emptyForm,
  ...data,

  require_open_cash_register:
    data.require_open_cash_register ?? true,

  allow_sales_without_stock:
    data.allow_sales_without_stock ?? false,

  require_customer:
    data.require_customer ?? false,

  allow_product_discounts:
    data.allow_product_discounts ?? true,

  max_discount_percent:
    data.max_discount_percent ?? 0,

  default_payment_method:
    data.default_payment_method || "cash",

  auto_print_receipt:
    data.auto_print_receipt ?? false,

  allow_returns:
    data.allow_returns ?? true,

  is_active:
    data.is_active ?? true,
});

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

export default function CompanySalesSettings({
  readOnly = false,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_URL}/settings/sales/me`,
        {
          headers: getHeaders(),
        }
      );

      setForm(normalizeSalesSettings(response.data));
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
          "No fue posible cargar la configuración de ventas."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (readOnly) {
      return;
    }

    const discount = Number(form.max_discount_percent || 0);

    if (discount < 0 || discount > 100) {
      toast.warning(
        "El descuento máximo debe estar entre 0 y 100 %."
      );
      return;
    }

    try {
      setSaving(true);

      const payload = {
        require_open_cash_register:
          form.require_open_cash_register,

        allow_sales_without_stock:
          form.allow_sales_without_stock,

        require_customer:
          form.require_customer,

        allow_product_discounts:
          form.allow_product_discounts,

        max_discount_percent:
          form.allow_product_discounts ? discount : 0,

        default_payment_method:
          form.default_payment_method,

        auto_print_receipt:
          form.auto_print_receipt,

        allow_returns:
          form.allow_returns,

        is_active:
          form.is_active,
      };

      const response = await axios.put(
        `${API_URL}/settings/sales/me`,
        payload,
        {
          headers: getHeaders(),
        }
      );

      setForm(normalizeSalesSettings(response.data));

      toast.success("Configuración de ventas actualizada.");
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
          "No fue posible guardar la configuración de ventas."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        Cargando configuración de ventas...
      </div>
    );
  }

  return (
    <form
      className="sales-settings-form"
      onSubmit={handleSubmit}
    >
      <div className="sales-settings-heading">
        <div>
          <h3>Configuración de ventas</h3>
          <p>
            Controla las reglas que se aplican al registrar ventas
            desde el POS.
          </p>
        </div>

        <span
          className={
            form.is_active
              ? "sales-status active"
              : "sales-status inactive"
          }
        >
          {form.is_active
            ? "Ventas activas"
            : "Ventas inactivas"}
        </span>
      </div>

      <fieldset
        className="sales-settings-fieldset"
        disabled={readOnly}
      >
        <section className="sales-settings-section">
          <h4>Reglas de operación</h4>

          <div className="sales-switches">
            <label className="sales-switch">
              <input
                type="checkbox"
                name="require_open_cash_register"
                checked={form.require_open_cash_register}
                onChange={handleChange}
              />
              <span>
                Exigir caja abierta antes de registrar una venta
              </span>
            </label>

            <label className="sales-switch">
              <input
                type="checkbox"
                name="allow_sales_without_stock"
                checked={form.allow_sales_without_stock}
                onChange={handleChange}
              />
              <span>
                Permitir ventas sin existencias disponibles
              </span>
            </label>

            <label className="sales-switch">
              <input
                type="checkbox"
                name="require_customer"
                checked={form.require_customer}
                onChange={handleChange}
              />
              <span>
                Solicitar cliente obligatoriamente en cada venta
              </span>
            </label>

            <label className="sales-switch">
              <input
                type="checkbox"
                name="allow_returns"
                checked={form.allow_returns}
                onChange={handleChange}
              />
              <span>
                Permitir anulaciones y reintegros de ventas
              </span>
            </label>
          </div>
        </section>

        <section className="sales-settings-section">
          <h4>Descuentos y pagos</h4>

          <div className="sales-settings-grid">
            <div className="floating-field">
              <select
                id="default_payment_method"
                name="default_payment_method"
                value={form.default_payment_method}
                onChange={handleChange}
              >
                <option value="cash">Efectivo</option>
                <option value="card">Tarjeta</option>
                <option value="transfer">
                  Transferencia
                </option>
                <option value="credit">Crédito</option>
              </select>
              <label htmlFor="default_payment_method">
                Medio de pago predeterminado
              </label>
            </div>

            <div className="floating-field">
              <input
                id="max_discount_percent"
                name="max_discount_percent"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.max_discount_percent}
                onChange={handleChange}
                placeholder=" "
                disabled={
                  readOnly ||
                  !form.allow_product_discounts
                }
              />
              <label htmlFor="max_discount_percent">
                Descuento máximo permitido (%)
              </label>
            </div>
          </div>

          <div className="sales-switches">
            <label className="sales-switch">
              <input
                type="checkbox"
                name="allow_product_discounts"
                checked={form.allow_product_discounts}
                onChange={handleChange}
              />
              <span>
                Permitir descuentos en productos
              </span>
            </label>

            <label className="sales-switch">
              <input
                type="checkbox"
                name="auto_print_receipt"
                checked={form.auto_print_receipt}
                onChange={handleChange}
              />
              <span>
                Imprimir comprobante automáticamente al finalizar
              </span>
            </label>
          </div>
        </section>

        <section className="sales-settings-section sales-danger-zone">
          <h4>Estado del módulo</h4>

          <label className="sales-switch">
            <input
              type="checkbox"
              name="is_active"
              checked={form.is_active}
              onChange={handleChange}
            />
            <span>
              Habilitar el módulo de ventas para esta empresa
            </span>
          </label>
        </section>
      </fieldset>

      <div className="sales-settings-actions">
        {!readOnly ? (
          <button type="submit" disabled={saving}>
            {saving
              ? "Guardando..."
              : "Guardar configuración"}
          </button>
        ) : (
          <span className="settings-view-only">
            Solo consulta
          </span>
        )}
      </div>
    </form>
  );
}